import { Select, Tag } from 'antd'
import { useTicketStepContext } from 'features/libra/ticket/TicketStateContext'
import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

export type SelectOption<T extends string | number> = {
  value: T
  label: string
  color?: string
}

function ReadOnlySelectValue<T extends string | number>({
  option,
}: {
  option: SelectOption<T> | undefined
}) {
  if (!option) return <>-</>

  return (
    <ReadOnlyContainer>
      {option.color ? (
        <Tag color={option.color}>{option.label}</Tag>
      ) : (
        <>{option.label}</>
      )}
    </ReadOnlyContainer>
  )
}

function RawEditableSelectInner<T extends string | number>({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  width = 200,
}: {
  value: T | undefined
  onChange?: (value: T) => void
  options: ReadonlyArray<SelectOption<T>>
  placeholder?: string
  width?: number | string
}) {
  const { isStepComplete } = useTicketStepContext()

  const valueToLabel = useMemo<ReadonlyMap<T, SelectOption<T>>>(
    () => new Map<T, SelectOption<T>>(options.map((o) => [o.value, o])),
    [options]
  )

  const handleChange = useCallback(
    (next: T) => {
      onChange?.(next)
    },
    [onChange]
  )

  const selectOptions = useMemo(
    () =>
      options.map((o) => ({
        value: o.value,
        label: o.color ? <Tag color={o.color}>{o.label}</Tag> : o.label,
      })),
    [options]
  )

  if (!onChange || isStepComplete) {
    const opt = value === undefined ? undefined : valueToLabel.get(value)
    return <ReadOnlySelectValue option={opt} />
  }

  return (
    <Select<T>
      style={{ width }}
      value={value}
      placeholder={placeholder}
      onChange={handleChange}
      options={selectOptions}
    />
  )
}

const RawEditableSelect = RawEditableSelectInner as <
  T extends string | number,
>(props: {
  value: T | undefined
  onChange?: (value: T) => void
  options: ReadonlyArray<SelectOption<T>>
  placeholder?: string
  width?: number | string
}) => JSX.Element

export const EditableSelect = memo(
  RawEditableSelect
) as typeof RawEditableSelect

const ReadOnlyContainer = styled.div`
  display: inline-flex;
`
